import { useState } from 'react';
import { Check, X, Plus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { StyledCard } from '@/components/ui/styled-card';
import { FullscreenDialog } from '@/components/ui/fullscreen-dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useMyOrganization } from '@/hooks/organization/use-my-organization';
import { useListMembers } from '@/hooks/organization/use-list-members';
import { useCreateGroupProposal } from '@/hooks/admin/group/use-create-group-proposal';
import { formatName } from '@/lib/name';
import { getEmailDisplay, type OrganizationMember } from '@/types/user';

interface NewGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGroupCreated?: () => void;
}

export function NewGroupDialog({
  open,
  onOpenChange,
  onGroupCreated,
}: NewGroupDialogProps) {
  const [groupName, setGroupName] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<OrganizationMember[]>(
    []
  );
  const [memberDropdownOpen, setMemberDropdownOpen] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  // Fetch organization and members
  const { organization } = useMyOrganization();
  const { members, isLoading: isLoadingMembers } = useListMembers(
    organization?.id ?? null
  );

  // Create proposal mutation
  const { createProposal, isLoading: isCreating } = useCreateGroupProposal({
    onSuccess: () => {
      // Reset form and close both dialogs
      setGroupName('');
      setSelectedMembers([]);
      setShowConfirmationDialog(false);
      onOpenChange(false);
      if (onGroupCreated) {
        onGroupCreated();
      }
    },
    onError: (error) => {
      console.error('Failed to create group proposal', error);
    },
  });

  const handleMemberToggle = (member: OrganizationMember) => {
    setSelectedMembers((prev) => {
      const isSelected = prev.some((m) => m.id === member.id);
      if (isSelected) {
        return prev.filter((m) => m.id !== member.id);
      } else {
        return [...prev, member];
      }
    });
  };

  const handleSubmit = () => {
    if (!groupName.trim() || selectedMembers.length === 0) {
      return;
    }

    // Show confirmation dialog instead of immediately creating the group
    setShowConfirmationDialog(true);
  };

  const handleConfirmation = async () => {
    if (!organization) return;

    await createProposal({
      groupName: groupName.trim(),
      userIds: selectedMembers.map((m) => m.id),
    });
  };

  const handleCancel = () => {
    // Reset form and close confirmation dialog if open
    setGroupName('');
    setSelectedMembers([]);
    setShowConfirmationDialog(false);
    onOpenChange(false);
  };

  const isFormValid = groupName.trim() && selectedMembers.length > 0;

  return (
    <>
      <FullscreenDialog
        open={open}
        onOpenChange={onOpenChange}
        title="Create a new group"
        footer={
          <div className="flex justify-center gap-2 max-w-xl w-full">
            <Button size="lg" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button size="lg" onClick={handleSubmit} disabled={!isFormValid}>
              Create Group
            </Button>
          </div>
        }
        bodyContainerClassName="bg-muted"
      >
        <div className="max-w-lg mx-auto space-y-6">
          {/* Group Name Section */}
          <StyledCard title="Group Name">
            <div className="space-y-4">
              <div className="space-y-2">
                <Input
                  id="groupName"
                  placeholder="Enter group name"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  size="lg"
                />
              </div>
            </div>
          </StyledCard>

          {/* Member Selection Section */}
          <StyledCard title={`Group Members (${selectedMembers.length})`}>
            <div className="space-y-4">
              {/* Selected Members */}
              <div className="space-y-2">
                {selectedMembers.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {selectedMembers.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center gap-2 px-3 py-1.5 bg-accent/10 rounded-full border border-accent/20"
                      >
                        <span className="text-sm font-medium">
                          {formatName(member.name)}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-4 w-4 p-0 hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => handleMemberToggle(member)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground ">
                    No members added
                  </div>
                )}
              </div>

              {/* Add Members Dropdown */}
              <div className="space-y-2">
                <Popover
                  open={memberDropdownOpen}
                  onOpenChange={setMemberDropdownOpen}
                >
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={memberDropdownOpen}
                      className="w-full justify-center"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add member
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[--radix-popover-trigger-width] p-0"
                    align="start"
                  >
                    <Command>
                      <CommandInput placeholder="Search members..." />
                      <CommandList>
                        {isLoadingMembers ? (
                          <div className="flex items-center justify-center p-4">
                            <Loader2 className="h-4 w-4 animate-spin" />
                          </div>
                        ) : (
                          <>
                            <CommandEmpty>No members found.</CommandEmpty>
                            <CommandGroup>
                              {members.map((member) => {
                                const isSelected = selectedMembers.some(
                                  (m) => m.id === member.id
                                );
                                return (
                                  <CommandItem
                                    key={member.id}
                                    value={`${member.name} ${getEmailDisplay(member.email)}`}
                                    onSelect={() => handleMemberToggle(member)}
                                    className="flex items-center justify-between p-2 cursor-pointer"
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className="text-sm font-medium">
                                        {formatName(member.name)}
                                      </div>
                                      <div className="text-sm text-muted-foreground">
                                        {getEmailDisplay(member.email)}
                                      </div>
                                    </div>
                                    <div className="flex items-center">
                                      {isSelected && (
                                        <Check className="h-4 w-4 text-primary" />
                                      )}
                                    </div>
                                  </CommandItem>
                                );
                              })}
                            </CommandGroup>
                          </>
                        )}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </StyledCard>
        </div>
      </FullscreenDialog>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmationDialog} onOpenChange={() => {}}>
        <DialogContent
          className="sm:max-w-md [&>button]:hidden"
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>New group must be approved by admins</DialogTitle>
            <DialogDescription>
              The new group will be created after it has been reviewed and
              approved by admins
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={handleConfirmation} disabled={isCreating}>
              {isCreating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Okay'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
